import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { apiClient } from '../services/api';
import { useAuth } from './AuthContext';
import type { AssistantMessage } from '../types';

interface AssistantContextValue {
  isOpen: boolean;
  isLoading: boolean;
  messages: AssistantMessage[];
  conversationId: string | null;
  togglePane: () => void;
  closePane: () => void;
  openPane: () => void;
  sendMessage: (message: string) => Promise<void>;
  startNewConversation: () => Promise<void>;
  submitFeedback: (messageIndex: number, isPositive: boolean, comment?: string) => Promise<void>;
}

const AssistantContext = createContext<AssistantContextValue | undefined>(undefined);

export function AssistantProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);

  const togglePane = useCallback(() => setIsOpen((previous) => !previous), []);
  const closePane = useCallback(() => setIsOpen(false), []);
  const openPane = useCallback(() => setIsOpen(true), []);

  const sendMessage = useCallback(async (message: string) => {
    if (!user?.tenantId) {
      return;
    }

    const now = new Date().toISOString();
    const userMessage: AssistantMessage = { role: 'user', content: message, timestamp: now };
    setMessages((previous) => [...previous, userMessage]);
    setIsLoading(true);
    try {
      const response = await apiClient.sendAssistantMessage(user.tenantId, {
        message,
        conversationId: conversationId ?? undefined,
      });
      setConversationId(response.conversationId ?? conversationId ?? null);
      const assistantMessages = (Array.isArray(response.messages) ? response.messages : [])
        .filter((m): m is AssistantMessage => m != null && typeof m.content === 'string');
      const lastAssistant = [...assistantMessages].reverse().find((m) => m.role === 'assistant');
      if (lastAssistant) {
        const assistantMessage: AssistantMessage = {
          role: 'assistant',
          content: lastAssistant.content ?? '',
          timestamp: lastAssistant.timestamp ?? new Date().toISOString(),
          suggestedActions: lastAssistant.suggestedActions,
          creditsCharged: lastAssistant.creditsCharged,
          operationType: lastAssistant.operationType,
        };
        setMessages((previous) => [...previous, assistantMessage]);
      }
    } catch (error: unknown) {
      let errorMessage = 'Beacon is unavailable right now. Please try again shortly.';
      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { message?: string } } };
        if (axiosError.response?.data?.message) {
          errorMessage = axiosError.response.data.message;
        }
      }

      setMessages((previous) => [
        ...previous,
        {
          role: 'assistant' as const,
          content: errorMessage,
          timestamp: new Date().toISOString(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [conversationId, user?.tenantId]);

  const startNewConversation = useCallback(async () => {
    setConversationId(null);
    setMessages([]);
  }, []);

  const submitFeedback = useCallback(async (messageIndex: number, isPositive: boolean, comment?: string) => {
    if (!user?.tenantId || !conversationId) {
      return;
    }

    // Find the user query that preceded this assistant message
    const assistantMsg = messages[messageIndex];
    const userMsg = messageIndex > 0 ? messages[messageIndex - 1] : undefined;

    try {
      await apiClient.submitAssistantFeedback(user.tenantId, {
        conversationId,
        messageIndex,
        isPositive,
        comment,
        userQuery: userMsg?.role === 'user' ? userMsg.content : undefined,
        assistantResponse: assistantMsg?.content,
      });

      setMessages((previous) =>
        previous.map((msg, idx) =>
          idx === messageIndex ? { ...msg, feedback: isPositive ? 'positive' : 'negative' } : msg
        )
      );
    } catch {
      // Silently fail — feedback is non-critical
    }
  }, [conversationId, messages, user?.tenantId]);

  const value = useMemo<AssistantContextValue>(() => ({
    isOpen,
    isLoading,
    messages,
    conversationId,
    togglePane,
    closePane,
    openPane,
    sendMessage,
    startNewConversation,
    submitFeedback,
  }), [
    isOpen,
    isLoading,
    messages,
    conversationId,
    togglePane,
    closePane,
    openPane,
    sendMessage,
    startNewConversation,
    submitFeedback,
  ]);

  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAssistant() {
  const context = useContext(AssistantContext);
  if (!context) {
    throw new Error('useAssistant must be used within AssistantProvider');
  }
  return context;
}
